"use client";

import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "sonner";
import {
  getCategoriesTreeApi,
  createCategoryApi,
  toggleCategoryStatusApi,
  getParentCategoriesApi,
  getProductsByCategoryIdApi,
  getProductsByCategorySlugApi,
  getSubCategoriesByParentIdApi,
  getProductsSubCategoryApi,
  deleteCategoryApi,
  updateCategoryApi,
} from "../api/categories.api";
import {
  Category,
  CategoryTreeNode,
  CategoriesResponse,
  CategoriesTreeResponse,
  SubCategoriesResponse,
  ProductsResponse,
  CreateCategoryResponse,
} from "@/types/category.types";

export const categoriesQueryKeys = {
  all: ["categories"] as const,
  lists: () => [...categoriesQueryKeys.all, "list"] as const,
  list: (filters?: Record<string, unknown>) =>
    [...categoriesQueryKeys.all, "list", { filters }] as const,
  tree: () => [...categoriesQueryKeys.all, "tree"] as const,
  parent: (page: number, limit: number) =>
    [...categoriesQueryKeys.all, "parent", { page, limit }] as const,
  sub: (parentId: string) =>
    [...categoriesQueryKeys.all, "sub", { parentId }] as const,
  products: (idOrSlug: string, page: number, limit: number) =>
    [...categoriesQueryKeys.all, "products", { idOrSlug, page, limit }] as const,
};

/**
 * 🔹 USE CATEGORIES TREE
 */
export const useCategoriesTree = (
  options?: Partial<UseQueryOptions<CategoryTreeNode[], AxiosError>>
) => {
  return useQuery({
    queryKey: categoriesQueryKeys.tree(),
    queryFn: async () => {
      const response = await getCategoriesTreeApi();
      return response.data;
    },
    staleTime: 1000 * 60 * 60, // 1 hour
    refetchOnWindowFocus: false,
    ...options,
  });
};

/**
 * 🔹 USE CREATE CATEGORY
 */
export const useCreateCategory = (
  options?: Partial<UseMutationOptions<CreateCategoryResponse, AxiosError, FormData, unknown>>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: FormData) => createCategoryApi(payload),
    onSuccess: (data, variables, context) => {
      toast.success("Catégorie créée avec succès.");
      queryClient.invalidateQueries({ queryKey: categoriesQueryKeys.all });
      (options?.onSuccess as any)?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      const message =
        (error.response?.data as any)?.message || "Erreur lors de la création.";
      toast.error(message);
      console.error("Failed to create category:", error);
      (options?.onError as any)?.(error, variables, context);
    },
    ...options,
  });
};

/**
 * 🔹 USE PARENT CATEGORIES
 */
export const useParentCategories = (
  page: number,
  limit: number,
  options?: Partial<UseQueryOptions<CategoriesResponse["data"], AxiosError>>
) => {
  return useQuery({
    queryKey: categoriesQueryKeys.parent(page, limit),
    queryFn: async () => {
      const response = await getParentCategoriesApi(page, limit);
      return response.data;
    },
    staleTime: 1000 * 60 * 10,
    ...options,
  });
};

/**
 * 🔹 USE ALL CATEGORIES (FLAT)
 */
export const useAllCategories = (
  options?: Partial<UseQueryOptions<Category[], AxiosError>>
) => {
  return useQuery({
    queryKey: [...categoriesQueryKeys.all, "flat"],
    queryFn: async () => {
      const response = await getParentCategoriesApi(1, 1000);
      return response.data?.data ?? [];
    },
    staleTime: 1000 * 60 * 10,
    ...options,
  });
};

/**
 * 🔹 USE PRODUCTS BY CATEGORY ID
 */
export const useProductsByCategoryId = (
  categoryId: string,
  page: number,
  limit: number,
  options?: Partial<UseQueryOptions<ProductsResponse["data"], AxiosError>>
) => {
  return useQuery({
    queryKey: categoriesQueryKeys.products(categoryId, page, limit),
    queryFn: async () => {
      const response = await getProductsByCategoryIdApi(categoryId, page, limit);
      return response.data;
    },
    staleTime: 1000 * 60 * 10,
    ...options,
  });
};

/**
 * 🔹 USE PRODUCTS BY CATEGORY SLUG
 */
export const useProductsByCategorySlug = (
  categorySlug: string,
  page: number,
  limit: number,
  options?: Partial<UseQueryOptions<ProductsResponse["data"], AxiosError>>
) => {
  return useQuery({
    queryKey: categoriesQueryKeys.products(categorySlug, page, limit),
    queryFn: async () => {
      const response = await getProductsByCategorySlugApi(
        categorySlug,
        page,
        limit
      );
      return response.data;
    },
    staleTime: 1000 * 60 * 10,
    enabled: !!categorySlug,
    ...options,
  });
};

/**
 * 🔹 USE SUB CATEGORIES BY PARENT ID
 */
export const useSubCategoriesByParentId = (
  parentId: string,
  options?: Partial<UseQueryOptions<Category[], AxiosError>>
) => {
  return useQuery({
    queryKey: categoriesQueryKeys.sub(parentId),
    queryFn: async () => {
      const response = await getSubCategoriesByParentIdApi(parentId);
      return response.data;
    },
    staleTime: 1000 * 60 * 10,
    ...options,
  });
};

/**
 * 🔹 USE PRODUCTS SUB CATEGORY
 */
export const useProductsSubCategory = (
  parentId: string,
  page: number,
  limit: number,
  options?: Partial<UseQueryOptions<ProductsResponse["data"], AxiosError>>
) => {
  return useQuery({
    queryKey: categoriesQueryKeys.products(parentId, page, limit),
    queryFn: async () => {
      const response = await getProductsSubCategoryApi(parentId, page, limit);
      return response.data;
    },
    staleTime: 1000 * 60 * 10,
    ...options,
  });
};

/**
 * 🔹 USE TOGGLE CATEGORY STATUS
 */
export const useToggleCategoryStatus = (
  options?: Partial<UseMutationOptions<{ success: boolean; message: string }, AxiosError, string, unknown>>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => toggleCategoryStatusApi(id),
    onSuccess: (data, variables, context) => {
      toast.success("Catégorie mise à jour.");
      queryClient.invalidateQueries({ queryKey: categoriesQueryKeys.all });
      (options?.onSuccess as any)?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      toast.error("Erreur lors de la mise à jour.");
      console.error("Failed to update category:", error);
      (options?.onError as any)?.(error, variables, context);
    },
    ...options,
  });
};

/**
 * 🔹 USE UPDATE CATEGORY
 */
export const useUpdateCategory = (
  options?: Partial<
    UseMutationOptions<CreateCategoryResponse, AxiosError, { id: string; payload: FormData }, unknown>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: FormData }) =>
      updateCategoryApi(id, payload),
    onSuccess: (data, variables, context) => {
      toast.success("Catégorie mise à jour avec succès.");
      queryClient.invalidateQueries({ queryKey: categoriesQueryKeys.all });
      (options?.onSuccess as any)?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      const message =
        (error.response?.data as any)?.message || "Erreur lors de la mise à jour.";
      toast.error(message);
      console.error("Failed to update category:", error);
      (options?.onError as any)?.(error, variables, context);
    },
    ...options,
  });
};

/**
 * 🔹 USE DELETE CATEGORY
 */
export const useDeleteCategory = (
  options?: Partial<UseMutationOptions<{ success: boolean; message: string }, AxiosError, string, unknown>>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteCategoryApi(id),
    onSuccess: (data, variables, context) => {
      toast.success("Catégorie supprimée.");
      queryClient.invalidateQueries({ queryKey: categoriesQueryKeys.all });
      (options?.onSuccess as any)?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      const message =
        (error.response?.data as any)?.message || "Erreur lors de la suppression.";
      toast.error(message);
      console.error("Failed to delete category:", error);
      (options?.onError as any)?.(error, variables, context);
    },
    ...options,
  });
};
